import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import colors from '../../style/colors';
import { spacing } from '../../style';
import { inputBorderColor } from '../../style/theme-helpers';
import { defaultBorderRadiusByComponentClass, inputBorderRadiusOptions, sizes, variants } from './style';

type Ref = HTMLInputElement | HTMLTextAreaElement;

export interface InputProps {
  componentClass?: React.ElementType;
  variant?: keyof typeof variants;
  focused?: boolean;
  block?: boolean;
  size?: keyof typeof sizes;
  active?: boolean;
  focusOnMount?: boolean;
  onDragStart?: () => void;
  onDrop?: () => void;
  'data-name'?: string;
  'data-action'?: string;
  onBlur?: (e?: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => void;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  dir?: 'ltr' | 'rtl' | 'auto';
  textAlign?: 'center' | 'left' | 'right';
  role?: 'input' | 'textbox';
  resize?: string;
  readOnly?: boolean;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  type?: 'text' | 'number' | 'password' | 'email' | 'tel' | 'url';
  value?: any;
  name?: string;
  maxLength?: number;
  id?: string;
  children?: React.ElementType | string;
  autocomplete?: HTMLInputElement['autocomplete'];
  'aria-controls'?: string;
  'aria-autocomplete'?: string;
  'aria-required'?: string | boolean;
  'aria-activedescendant'?: string;
  'aria-expanded'?: string;
  'aria-haspopup'?: string;
  'aria-invalid'?: string | boolean;
  'aria-describedby'?: string;
  'data-testid'?: string;
  borderRadius?: keyof typeof inputBorderRadiusOptions;
  ref?: React.Ref<Ref | null>;
  min?: number;
  max?: number;
  error?: boolean;
  style?: React.CSSProperties;
  onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void;
}

export const DEFAULT_INPUT_SIZE = 'lg';
export const DEFAULT_COMPONENT = 'input';

const InputComponent = ({
  ref,
  componentClass: Component,
  className,
  ariaLabel,
  disabled,
  focused,
  block = true,
  focusOnMount,
  placeholder,
  type = 'text',
  value = '',
  name,
  maxLength,
  onBlur,
  onChange,
  onFocus,
  readOnly,
  active,
  onKeyDown,
  onClick,
  onDragStart,
  onDrop,
  'data-name': dataName,
  'data-action': dataAction,
  role,
  id,
  size,
  children,
  autocomplete,
  'aria-controls': ariaControls,
  'aria-autocomplete': ariaAutocomplete,
  'aria-required': ariaRequired,
  'aria-activedescendant': ariaActivedescendant,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'data-testid': dataTestId,
  onMouseDown,
  dir,
  min,
  max,
  error,
  style,
}: InputProps) => {
  const localRef = React.useRef<Ref | null>(null);

  const setRef = (node: Ref | null) => {
    localRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  React.useEffect(() => {
    if (focusOnMount) {
      localRef.current?.focus();
    }
  }, [focusOnMount]);

  const ResolvedInput = Component || sizes[size || DEFAULT_INPUT_SIZE]?.componentClass || DEFAULT_COMPONENT;

  return (
    <ResolvedInput
      ref={setRef}
      className={classNames(
        'box-input',
        className,
        { disabled },
        { block },
        { focus: focused },
        { active },
        { error },
        Component && `${Component}`,
      )}
      style={style}
      maxLength={maxLength}
      disabled={disabled}
      onBlur={onBlur}
      onMouseDown={onMouseDown}
      onChange={onChange}
      onFocus={onFocus}
      readOnly={readOnly}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      type={type}
      value={value}
      name={name}
      data-name={dataName}
      data-action={dataAction}
      onClick={onClick}
      onDragStart={onDragStart}
      onDrop={onDrop}
      role={role}
      id={id}
      aria-label={ariaLabel || placeholder}
      autoComplete={autocomplete}
      aria-controls={ariaControls}
      aria-autocomplete={ariaAutocomplete}
      aria-required={ariaRequired}
      aria-activedescendant={ariaActivedescendant}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      dir={dir}
      data-testid={dataTestId}
      min={min}
      max={max}
    >
      {children}
    </ResolvedInput>
  );
};

InputComponent.displayName = 'InputComponent';

const Input = styled(InputComponent)<InputProps>`
  border-radius: ${props =>
    inputBorderRadiusOptions[
      props.borderRadius ||
        defaultBorderRadiusByComponentClass[sizes[props.size || DEFAULT_INPUT_SIZE]?.componentClass || 'input']
    ]};

  font-size: ${props => sizes[props.size || DEFAULT_INPUT_SIZE]?.fontSize};
  text-align: ${props => props.textAlign};
  border: 1px solid ${inputBorderColor};
  resize: ${props => props.resize || 'none'};
  height: ${props => sizes[props.size || DEFAULT_INPUT_SIZE]?.height};
  box-shadow: none;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  padding: ${spacing.xs} ${spacing.sm};
  outline: none;
  color: ${colors.gray900};

  ${props => variants[props.variant || 'action']};

  &.error {
    border-color: ${colors.negative};
  }

  line-height: 1.42857;

  fieldset[disabled] &,
  &.disabled,
  &[disabled] {
    background: ${colors.gray50};
    color: ${colors.gray600};
    cursor: not-allowed;
  }

  &.block {
    width: 100%;
  }

  &.textarea {
    font-size: 1rem;
    line-height: 1.5;
  }
`;

export default Input;
